import React, { useState, useEffect } from 'react';
import { FileCheck, AlertTriangle, Upload, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

const StockReconciliation = ({ outlets, products, selectedOutlet: parentSelectedOutlet }) => {
  const [reconciliations, setReconciliations] = useState([]);
  const [dumps, setDumps] = useState([]);
  const [showReconcile, setShowReconcile] = useState(false);
  const [showDump, setShowDump] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState(parentSelectedOutlet || 'main');
  
  const [reconData, setReconData] = useState({
    outlet_id: parentSelectedOutlet || 'main',
    product_id: '',
    physical_stock: '',
    reconciled_by: 'Admin',
    notes: ''
  });

  const [dumpData, setDumpData] = useState({
    outlet_id: parentSelectedOutlet || 'main',
    product_id: '',
    quantity: '',
    reason: '',
    photos: [],
    reported_by: 'Admin'
  });

  // Sync with parent outlet selection
  useEffect(() => {
    if (parentSelectedOutlet && parentSelectedOutlet !== 'all') {
      setSelectedOutlet(parentSelectedOutlet);
      setReconData(prev => ({ ...prev, outlet_id: parentSelectedOutlet }));
      setDumpData(prev => ({ ...prev, outlet_id: parentSelectedOutlet }));
    }
  }, [parentSelectedOutlet]);

  useEffect(() => {
    fetchReconciliations();
    fetchDumps();
  }, [selectedOutlet, parentSelectedOutlet]);

  const fetchReconciliations = async () => {
    try {
      // If "all" is selected, fetch all reconciliations
      const outletParam = parentSelectedOutlet === 'all' ? '' : `outlet_id=${selectedOutlet}`;
      const response = await api.get(`/reconciliation?${outletParam}`);
      setReconciliations(response.data);
    } catch (error) {
      console.error('Error fetching reconciliations:', error);
    }
  };

  const fetchDumps = async () => {
    try {
      // If "all" is selected, fetch all dumps
      const outletParam = parentSelectedOutlet === 'all' ? '' : `outlet_id=${selectedOutlet}`;
      const response = await api.get(`/dump?${outletParam}`);
      setDumps(response.data);
    } catch (error) {
      console.error('Error fetching dumps:', error);
    }
  };

  const handleReconcile = async () => {
    if (!reconData.product_id || !reconData.physical_stock) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await api.post('/reconciliation', {
        ...reconData,
        physical_stock: parseInt(reconData.physical_stock)
      });
      toast.success('Stock reconciled successfully');
      setShowReconcile(false);
      setReconData({
        outlet_id: selectedOutlet,
        product_id: '',
        physical_stock: '',
        reconciled_by: 'Admin',
        notes: ''
      });
      fetchReconciliations();
    } catch (error) {
      toast.error('Failed to reconcile stock');
    }
  };

  const handleDump = async () => {
    if (!dumpData.product_id || !dumpData.quantity || !dumpData.reason) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await api.post('/dump', {
        ...dumpData,
        quantity: parseInt(dumpData.quantity)
      });
      toast.success('Wastage recorded successfully');
      setShowDump(false);
      setDumpData({
        outlet_id: selectedOutlet,
        product_id: '',
        quantity: '',
        reason: '',
        photos: [],
        reported_by: 'Admin'
      });
      fetchDumps();
    } catch (error) {
      toast.error('Failed to record wastage');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>
            Stock Reconciliation
          </h2>
          <p className="text-gray-400 text-sm">Physical stock verification and wastage tracking</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => setShowReconcile(true)}
            className="bg-[#00E5FF] hover:bg-[#00B8D4] text-black"
          >
            <FileCheck className="w-4 h-4 mr-2" />
            Reconcile Stock
          </Button>
          <Button
            onClick={() => setShowDump(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Record Wastage
          </Button>
        </div>
      </div>

      <Tabs defaultValue="reconciliation" className="w-full">
        <TabsList className="bg-[#121212] border border-white/10 mb-6">
          <TabsTrigger value="reconciliation">Reconciliation History</TabsTrigger>
          <TabsTrigger value="wastage">Wastage Records</TabsTrigger>
        </TabsList>

        {/* Reconciliation Tab */}
        <TabsContent value="reconciliation">
          <div className="space-y-4">
            {reconciliations.map(recon => (
              <Card key={recon.id} className="bg-[#121212] border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white mb-2">{recon.product_name}</h3>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">System Stock</p>
                          <p className="text-white font-bold">{recon.system_stock}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Physical Stock</p>
                          <p className="text-white font-bold">{recon.physical_stock}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Difference</p>
                          <p className={`font-bold ${
                            recon.difference > 0 ? 'text-green-400' : 
                            recon.difference < 0 ? 'text-red-400' : 'text-gray-400'
                          }`}>
                            {recon.difference > 0 ? '+' : ''}{recon.difference}
                          </p>
                        </div>
                      </div>
                      {recon.notes && (
                        <p className="text-sm text-gray-400 mt-2">Note: {recon.notes}</p>
                      )}
                    </div>
                    <div className="text-right text-sm text-gray-500">
                      <p>By: {recon.reconciled_by}</p>
                      <p>{new Date(recon.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Wastage Tab */}
        <TabsContent value="wastage">
          <div className="space-y-4">
            {dumps.map(dump => (
              <Card key={dump.id} className="bg-[#121212] border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white mb-2">{dump.product_name}</h3>
                      <div className="space-y-1 text-sm">
                        <p className="text-red-400 font-bold">Wasted: {dump.quantity} units</p>
                        <p className="text-gray-400">Reason: {dump.reason}</p>
                        <p className="text-gray-500">Reported by: {dump.reported_by}</p>
                      </div>
                      {dump.photos && dump.photos.length > 0 && (
                        <div className="mt-2 flex gap-2">
                          {dump.photos.map((photo, idx) => (
                            <img key={idx} src={photo} alt="Wastage" className="w-16 h-16 object-cover rounded" />
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-right text-sm text-gray-500">
                      <p>{new Date(dump.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Reconcile Dialog */}
      <Dialog open={showReconcile} onOpenChange={setShowReconcile}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>
              Reconcile Stock
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white mb-2 block">Product *</Label>
              <Select value={reconData.product_id} onValueChange={(value) => setReconData({...reconData, product_id: value})}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} (System: {p.stock})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white mb-2 block">Physical Stock Count *</Label>
              <Input
                type="number"
                value={reconData.physical_stock}
                onChange={(e) => setReconData({...reconData, physical_stock: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Enter actual count"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Notes</Label>
              <Input
                value={reconData.notes}
                onChange={(e) => setReconData({...reconData, notes: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Any observations..."
              />
            </div>
            <Button
              onClick={handleReconcile}
              className="w-full bg-[#00E5FF] hover:bg-[#00B8D4] text-black py-6 text-lg font-bold"
            >
              Reconcile & Update
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dump Dialog */}
      <Dialog open={showDump} onOpenChange={setShowDump}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>
              Record Wastage
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white mb-2 block">Product *</Label>
              <Select value={dumpData.product_id} onValueChange={(value) => setDumpData({...dumpData, product_id: value})}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white mb-2 block">Quantity *</Label>
              <Input
                type="number"
                value={dumpData.quantity}
                onChange={(e) => setDumpData({...dumpData, quantity: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Units wasted"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Reason *</Label>
              <Select value={dumpData.reason} onValueChange={(value) => setDumpData({...dumpData, reason: value})}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="spoiled">Spoiled</SelectItem>
                  <SelectItem value="damaged">Damaged</SelectItem>
                  <SelectItem value="burnt">Burnt/Overcooked</SelectItem>
                  <SelectItem value="contaminated">Contaminated</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white mb-2 block">Photo Evidence (Optional)</Label>
              <div className="bg-white/5 border border-white/10 rounded-lg p-4 text-center">
                <Upload className="w-8 h-8 mx-auto text-gray-500 mb-2" />
                <p className="text-sm text-gray-400">Photo upload feature coming soon</p>
              </div>
            </div>
            <Button
              onClick={handleDump}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg font-bold"
            >
              Record Wastage
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockReconciliation;